import { GameId, Jurisdiction } from '@bg-shared/enums';
import { envConfig } from '@bg-shared/constants/envConfig';
import {
    IAddedPartnerSettings,
    IEnabledGame,
    IEnabledPLSGame,
    IGameLimits,
    IResponseInitial,
} from '@bg-shared/infrastructure/Initial/interfaces';
import { sessionStore } from '@bg-shared/business/Session';
import { ClassicGameDefinition } from '@bg-shared/utils/ClassicGameDefinition';
import { ICurrency } from '@bg-shared/interfaces/ICurrency';
import { WebIntegrationType } from '@bg-shared/integrations/WebIntegration/enums';
import { BGI_BETTING_TIMEOUT, DEFAULT_BETTING_TIMEOUT } from '@bg-shared/constants/constants';
import { isInHouseGame } from './utils';
import { IGamesCategories, IPartnerSettingsModel, IStreamsDelays } from './interfaces';
import { RTL_LANGUAGES } from './constants';

export default class PartnerSettingsStore {
    private state: IPartnerSettingsModel;

    private getSymbol(template: string): string {
        return template.replace(/\s|X/g, '');
    }

    public update(data: IResponseInitial & IAddedPartnerSettings, language: string): void {
        const currency = sessionStore.session.currency ?? data.currency;

        this.state = {
            availableGamesIds: data.enabledGames.map((game) => game.gameId),
            isGamificationEnabled: data.isGamificationEnabled,
            betAmounts: sessionStore.bettingAmounts ?? (data.betAmounts?.map(parseFloat) || []),
            partnerCurrency: {
                ...data.partnerCurrency,
                symbol: this.getSymbol(data.partnerCurrency.template),
            },
            currency: {
                ...currency,
                symbol: this.getSymbol(currency.template),
            },
            isCombinationEnabled: data.isCombinationEnabled,
            isSubscriptionsEnabled: data.isSubscriptionsEnabled,
            isSingleRepeatBetEnabled: data.isSingleRepeatBetEnabled,
            isRtl: RTL_LANGUAGES.includes(language),
            isNotificationEnabled: data.isNotificationEnabled,
            isNumpadEnabled: data.isNumpadEnabled,
            isFullscreenEnabled: data.isFullscreenEnabled,
            isCasinoViewEnabled: data.isCasinoViewEnabled,
            isCasinoHintShown: data.isCasinoHintShown,
            isApiSendBalancePerGame: data.isApiSendBalancePerGame,
            showCollapsibleTableButton: data.showCollapsibleTableButton,
            isBettingFromStatisticsAllowed: data.isBettingFromStatisticsAllowed,
            isLimitInfoShown: data.isLimitInfoShown,
            isDefaultCasinoMode: data.isDefaultCasinoMode,
            isSurveillanceEnabled: data.isSurveillanceEnabled,
            jurisdiction: data.jurisdiction,
            possibleRunsCount: data.possibleRunsCount,
            rememberLastStake: data.rememberLastStake,
            disableVideoOnInactivity: data.disableVideoOnInactivity,
            reinitSessionOnGameNavigation: data.reinitSessionOnGameNavigation,
            showMessagesOnChangedOdds: data.showMessagesOnChangedOdds,
            showPlayersBetsSum: data.showPlayersBetsSum,
            showClock: data.showClock,
            showGuiTip: data.showGuiTip,
            showBalance: data.showBalance,
            integrationType: data.integrationType,
            streamsDelays: data.enabledGames.reduce(
                (delays, game) => {
                    if (isInHouseGame(game)) {
                        delays[game.gameId] = {
                            delay: game.streamDelay,
                            rngStartDelay: game.startDelay,
                        };
                    }
                    return delays;
                },
                {} as Record<GameId, IStreamsDelays>,
            ),
            topWonAmountsCurrency: data.topWonAmountsCurrency,
            logos: data.logos,
            isGeoBlocked: data.isGeoBlocked,
            refreshTokenFrequency: data.refreshTokenFrequency,
            webSocketUrl: data.webSocketUrl,
            enabledGames: data.enabledGames,
            taxes: data.taxes,
            cssCustom: data.cssCustom,
            now: data.now,
            isOppositeBettingRestricted: data.isOppositeBettingRestricted,
            isRouletteOppositeBettingRestricted: data.isRouletteOppositeBettingRestricted,
            isOutsideBetsRestricted: data.isOutsideBetsRestricted,
            screenName: data.screenName,
            gamesCategories: data.gamesCategories,
        };

        // TODO remove when data will be available
        if (
            (envConfig.isDevEnv || envConfig.isStagingEnv) &&
            this.state.gamesCategories.SLOTS &&
            !this.state.gamesCategories.SLOTS.includes(GameId.LUCKY_LENNY)
        ) {
            this.state.gamesCategories.SLOTS.push(GameId.LUCKY_LENNY);
        }
    }

    public get availableGamesIds(): GameId[] {
        return this.state.availableGamesIds;
    }

    public get availableInHouseGamesIds(): GameId[] {
        return this.state.availableGamesIds.filter(
            (gameId) =>
                !ClassicGameDefinition.isPLSGame(gameId) && !ClassicGameDefinition.isHZGame(gameId),
        );
    }

    public get availablePLSGamesIds(): GameId[] {
        return this.state.availableGamesIds.filter((gameId) =>
            ClassicGameDefinition.isPLSGame(gameId),
        );
    }

    public get isRtl(): boolean {
        return this.state.isRtl;
    }

    public get showBalance(): boolean {
        return this.state.showBalance;
    }

    public get partnerSettings(): IPartnerSettingsModel {
        return this.state;
    }

    public get gamesDelays(): Record<string, number> {
        return Object.fromEntries(
            Object.entries(this.state.streamsDelays).map(([key, game]) => [key, game.delay]),
        );
    }

    public get enabledInHouseGames(): IEnabledGame[] {
        return this.state.enabledGames.filter(isInHouseGame);
    }

    public get enabledGames(): (IEnabledGame | IEnabledPLSGame)[] {
        return this.state.enabledGames;
    }

    public get gamesPresetIds(): Record<string, number> {
        return Object.fromEntries(
            this.enabledInHouseGames.map((game) => [game.gameId, game.presetId]),
        );
    }

    public get gamesRngDelays(): Record<string, number> {
        return Object.fromEntries(
            Object.entries(this.state.streamsDelays).map(([key, game]) => [
                key,
                game.rngStartDelay,
            ]),
        );
    }

    public get partnerCurrency(): ICurrency {
        return this.state.partnerCurrency;
    }

    public get currency(): ICurrency {
        return this.state.currency;
    }

    public get currencySymbol(): string {
        return this.state.currency.symbol;
    }

    public getGameDelay(gameId: GameId): number {
        return this.state.streamsDelays[gameId].delay;
    }

    public get isSingleRepeatBetEnabled(): boolean {
        return this.state.isSingleRepeatBetEnabled;
    }

    public get isFullscreenEnabled(): boolean {
        return this.state.isFullscreenEnabled;
    }

    public get isCasinoViewEnabled(): boolean {
        return this.state.isCasinoViewEnabled;
    }

    public get isDefaultCasinoMode(): boolean {
        return this.state.isDefaultCasinoMode;
    }

    public get isNotificationEnabled(): boolean {
        return this.state.isNotificationEnabled;
    }

    public get isApiSendBalancePerGame(): boolean {
        return this.state.isApiSendBalancePerGame;
    }

    public get isCasinoHintShown(): boolean {
        return this.state.isCasinoHintShown;
    }

    public get isOppositeBettingRestricted(): boolean {
        return this.state?.isOppositeBettingRestricted;
    }

    public get isRouletteOppositeBettingRestricted(): boolean {
        return this.state?.isRouletteOppositeBettingRestricted;
    }

    public get isOutsideBetsRestricted(): boolean {
        return this.state?.isOutsideBetsRestricted;
    }

    public getCashOutOnDisconnectThreshold(gameId: GameId): number | null {
        return (
            this.enabledInHouseGames.find((game) => game.gameId === gameId)
                ?.disconnectCashOutThreshold || null
        );
    }

    public isAutoPlayEnabled(gameId: GameId): boolean {
        return (
            this.enabledInHouseGames.find((game) => game.gameId === gameId)?.autoPlay.enabled ||
            false
        );
    }

    public getAutoplayRounds(gameId: GameId): number[] {
        return (
            this.enabledInHouseGames.find((game) => game.gameId === gameId)?.autoPlay
                .roundButtons || []
        );
    }

    public getVersion(gameId: GameId): string {
        return this.enabledInHouseGames.find((game) => game.gameId === gameId)?.version || '';
    }

    public getEnabledGame(gameId: GameId): IEnabledGame {
        return this.enabledInHouseGames.find((game) => game.gameId === gameId);
    }

    public get webIntegrationType(): WebIntegrationType | null {
        return this.state?.integrationType;
    }

    public get requestTimeout(): number {
        return this.state?.integrationType === WebIntegrationType.BGI
            ? BGI_BETTING_TIMEOUT
            : DEFAULT_BETTING_TIMEOUT;
    }

    public get taxSchemeId(): number {
        return this.state.taxes?.schemeId ?? 1;
    }

    public get disableVideoOnInactivity(): boolean {
        return this.state.disableVideoOnInactivity;
    }

    public get isBrazilJurisdiction(): boolean {
        return this.state.jurisdiction === Jurisdiction.BRAZIL;
    }

    public get isUnitedKingdomMarket(): boolean {
        return this.state.jurisdiction === Jurisdiction.UNITED_KINGDOM;
    }

    public get isLithuaniaJurisdiction(): boolean {
        return this.state.jurisdiction === Jurisdiction.LITHUANIA;
    }

    public get isRealtimeBalanceEnabled(): boolean {
        return this.isBrazilJurisdiction || this.isLithuaniaJurisdiction;
    }

    get isCollapsibleTable(): boolean {
        return this.state.showCollapsibleTableButton;
    }

    public get isBettingFromStatisticsAllowed(): boolean {
        return this.state.isBettingFromStatisticsAllowed;
    }

    public get gamesCategories(): IGamesCategories {
        return this.state.gamesCategories;
    }

    public get isLimitInfoShown(): boolean {
        return this.state.isLimitInfoShown;
    }

    public isTaxEnabledForGame(gameId: GameId): boolean {
        return this.enabledInHouseGames.find((game) => game.gameId === gameId).taxesEnabled;
    }

    public isTipsEnabledForGame(gameId: GameId): boolean {
        return this.enabledInHouseGames.find((game) => game.gameId === gameId)?.tipsEnabled;
    }

    public getBetLimits(gameId: GameId): IGameLimits['limitsSettings'] {
        return this.state.enabledGames.find((game) => game.gameId === gameId)?.limitsSettings;
    }

    public isChatEnabled(gameId: GameId): boolean {
        return this.enabledInHouseGames.find((game) => game.gameId === gameId)?.chatEnabled;
    }

    public filterAvailableGamesIds(gameIds: GameId[]): void {
        this.state.availableGamesIds = this.state.availableGamesIds.filter(
            (gameId) => ClassicGameDefinition.isPLSGame(gameId) || gameIds.includes(gameId),
        );
    }
}
